/**
 * PDP product image zoom:
 * - Desktop: click image to magnify, move to pan, leave/outside to close (smooth zoom-out).
 * - Mobile: full-screen lightbox; pinch to zoom + pan inside the same lightbox.
 */

const ZOOM_SCALE = 2;
const DESKTOP_ZOOM_OUT_MS = 380;
const DESKTOP_ZOOM_OUT_EASING = "cubic-bezier(0.33, 1, 0.68, 1)";
const DESKTOP_MQ = "(min-width: 768px)";
const INIT_ATTR = "data-zoom-initialized";

const LIGHTBOX_ID = "product-image-zoom-lightbox";

function preloadHiRes(img, host) {
  const zoomSrc = host.dataset.zoomSrc;
  if (!zoomSrc || img.dataset.zoomLoaded === "true") {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const probe = new Image();
    probe.onload = () => {
      img.src = zoomSrc;
      img.removeAttribute("srcset");
      img.dataset.zoomLoaded = "true";
      resolve();
    };
    probe.onerror = () => resolve();
    probe.src = zoomSrc;
  });
}

function clearTransform(img) {
  img.style.transform = "";
  img.style.transformOrigin = "";
}

function updateDesktopZoom(host, img, clientX, clientY) {
  img.style.transition = "none";
  const rect = host.getBoundingClientRect();
  const w = rect.width || 1;
  const h = rect.height || 1;
  const x = ((clientX - rect.left) / w) * 100;
  const y = ((clientY - rect.top) / h) * 100;
  const ox = Math.min(100, Math.max(0, x));
  const oy = Math.min(100, Math.max(0, y));
  img.style.transformOrigin = `${ox}% ${oy}%`;
  img.style.transform = `scale(${ZOOM_SCALE})`;
}

function lockBodyScroll() {
  const y = window.scrollY || 0;
  document.body.dataset.scrollLockY = String(y);
  document.body.style.position = "fixed";
  document.body.style.top = `-${y}px`;
  document.body.style.left = "0";
  document.body.style.right = "0";
  document.body.style.width = "100%";
  document.body.classList.add("product-image-zoom-lightbox-open");
}

function unlockBodyScroll() {
  const y = parseInt(document.body.dataset.scrollLockY || "0", 10) || 0;
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  document.body.style.width = "";
  document.body.classList.remove("product-image-zoom-lightbox-open");
  delete document.body.dataset.scrollLockY;
  window.scrollTo(0, y);
}

function closeLightbox() {
  const root = document.getElementById(LIGHTBOX_ID);
  if (!root || root.hidden) return;
  if (typeof root._resetLightboxZoom === "function") {
    root._resetLightboxZoom();
  }
  const lbImg = root.querySelector(".product-image-zoom-lightbox__img");
  root.hidden = true;
  if (lbImg) {
    lbImg.removeAttribute("src");
    lbImg.alt = "";
  }
  unlockBodyScroll();
}

const LIGHTBOX_PINCH_MAX = 4;
const LIGHTBOX_PINCH_MIN = 1;

function attachLightboxPinchPan(root) {
  if (root.dataset.lightboxPinchBound === "true") return;

  const pinch = root.querySelector("[data-lightbox-pinch]");
  const img = root.querySelector(".product-image-zoom-lightbox__img");
  if (!pinch || !img) return;

  let scale = 1;
  let tx = 0;
  let ty = 0;
  let pinchStartDist = 0;
  let pinchStartScale = 1;
  let panStartX = 0;
  let panStartY = 0;
  let panOriginTx = 0;
  let panOriginTy = 0;

  function applyTransform() {
    img.style.transformOrigin = "center center";
    img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
  }

  function resetZoom() {
    scale = 1;
    tx = 0;
    ty = 0;
    applyTransform();
  }

  root._resetLightboxZoom = resetZoom;

  pinch.addEventListener(
    "touchstart",
    (e) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchStartDist = Math.hypot(dx, dy);
        pinchStartScale = scale;
      } else if (e.touches.length === 1 && scale > 1.02) {
        panStartX = e.touches[0].clientX;
        panStartY = e.touches[0].clientY;
        panOriginTx = tx;
        panOriginTy = ty;
      }
    },
    { passive: true }
  );

  pinch.addEventListener(
    "touchmove",
    (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        if (pinchStartDist > 0) {
          let next = pinchStartScale * (dist / pinchStartDist);
          next = Math.min(LIGHTBOX_PINCH_MAX, Math.max(LIGHTBOX_PINCH_MIN, next));
          scale = next;
          applyTransform();
        }
      } else if (e.touches.length === 1 && scale > 1.02) {
        e.preventDefault();
        const t = e.touches[0];
        tx = panOriginTx + (t.clientX - panStartX);
        ty = panOriginTy + (t.clientY - panStartY);
        applyTransform();
      }
    },
    { passive: false }
  );

  const onTouchEndOrCancel = (e) => {
    if (e.touches.length < 2) {
      pinchStartDist = 0;
    }
    if (e.touches.length === 1 && scale > 1.02) {
      panStartX = e.touches[0].clientX;
      panStartY = e.touches[0].clientY;
      panOriginTx = tx;
      panOriginTy = ty;
    }
    if (e.touches.length === 0 && scale <= 1.02) {
      resetZoom();
    }
  };

  pinch.addEventListener("touchend", onTouchEndOrCancel, { passive: true });
  pinch.addEventListener("touchcancel", onTouchEndOrCancel, { passive: true });

  root.dataset.lightboxPinchBound = "true";
}

function migrateLightboxPinchWrapper(root) {
  const inner = root.querySelector(".product-image-zoom-lightbox__inner");
  const img = root.querySelector(".product-image-zoom-lightbox__img");
  if (!inner || !img || root.querySelector("[data-lightbox-pinch]")) return;
  const pinch = document.createElement("div");
  pinch.className = "product-image-zoom-lightbox__pinch";
  pinch.setAttribute("data-lightbox-pinch", "");
  inner.replaceChildren(pinch);
  pinch.appendChild(img);
}

function ensureLightbox() {
  let root = document.getElementById(LIGHTBOX_ID);
  if (!root) {
    root = document.createElement("div");
    root.id = LIGHTBOX_ID;
    root.className = "product-image-zoom-lightbox";
    root.hidden = true;
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    root.setAttribute("aria-label", "Product image");
    root.innerHTML = `
    <button type="button" class="product-image-zoom-lightbox__close button-reset" aria-label="Close">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false">
        <path d="M18 6 6 18M6 6l12 12"/>
      </svg>
    </button>
    <div class="product-image-zoom-lightbox__inner">
      <div class="product-image-zoom-lightbox__pinch" data-lightbox-pinch>
        <img class="product-image-zoom-lightbox__img" alt="" />
      </div>
    </div>
  `;

    root
      .querySelector(".product-image-zoom-lightbox__close")
      .addEventListener("click", closeLightbox);
    root.addEventListener("click", (e) => {
      if (e.target === root) closeLightbox();
    });

    document.body.appendChild(root);
  } else {
    migrateLightboxPinchWrapper(root);
  }

  attachLightboxPinchPan(root);
}

if (!window.__productImageZoomLightboxInit) {
  window.__productImageZoomLightboxInit = true;
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    const root = document.getElementById(LIGHTBOX_ID);
    if (!root || root.hidden) return;
    closeLightbox();
  });
}

function openMobileLightbox(src, alt) {
  if (!src) return;
  ensureLightbox();
  const root = document.getElementById(LIGHTBOX_ID);
  if (typeof root._resetLightboxZoom === "function") {
    root._resetLightboxZoom();
  }
  const lbImg = root.querySelector(".product-image-zoom-lightbox__img");
  lbImg.alt = alt || "";
  lbImg.src = src;
  root.hidden = false;
  lockBodyScroll();
}
function bindHost(host) {
  if (host.getAttribute(INIT_ATTR) === "true") return;

  const img = host.querySelector("img.product-media__primary-image");
  if (!img || !host.dataset.zoomSrc) return;

  host.setAttribute(INIT_ATTR, "true");
  img.setAttribute("draggable", "false");

  let hiResPromise = Promise.resolve();
  let inlineZoomActive = false;
  let inlineZoomClosing = false;
  let zoomCloseFallbackId = 0;

  const finishZoomCleanup = () => {
    clearTimeout(zoomCloseFallbackId);
    if (!inlineZoomClosing) return;
    inlineZoomClosing = false;
    img.style.transition = "";
    clearTransform(img);
    host.classList.remove("product-media__zoom--settling");

    host.style.touchAction = ""; 
  };

  const closeInlineZoom = ({ instant = false } = {}) => {
    if (instant) {
      clearTimeout(zoomCloseFallbackId);
      inlineZoomClosing = false;
      inlineZoomActive = false;
      document.removeEventListener("pointerdown", onDocPointerDown, true);
      host.classList.remove("product-media__zoom--active", "product-media__zoom--settling");
      img.style.transition = "";
      clearTransform(img);
      host.style.touchAction = "";
      return;
    }

    if (!inlineZoomActive) return;
    inlineZoomActive = false;
    document.removeEventListener("pointerdown", onDocPointerDown, true);
    host.classList.remove("product-media__zoom--active");

    inlineZoomClosing = true;
    host.classList.add("product-media__zoom--settling");

    img.style.transition = `transform ${DESKTOP_ZOOM_OUT_MS}ms ${DESKTOP_ZOOM_OUT_EASING}`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        img.style.transform = "scale(1)";
      });
    });

    const onTransitionEnd = (e) => {
      if (e.propertyName !== "transform") return;
      img.removeEventListener("transitionend", onTransitionEnd);
      clearTimeout(zoomCloseFallbackId);
      finishZoomCleanup();
    };
    img.addEventListener("transitionend", onTransitionEnd);

    zoomCloseFallbackId = window.setTimeout(() => {
      img.removeEventListener("transitionend", onTransitionEnd);
      finishZoomCleanup();
    }, DESKTOP_ZOOM_OUT_MS + 80);
  };

  const onDocPointerDown = (e) => {
    if (!inlineZoomActive) return;
    if (host.contains(e.target)) return;
    closeInlineZoom();
  };

  const onHostClick = (e) => {
    const onMagnify = e.target.closest("[data-product-zoom-open]");
    const onImage = e.target.closest(".product-media__primary-image");
    
    if (!onMagnify && !onImage) return;
    if (inlineZoomClosing) return;

    e.preventDefault();
    e.stopPropagation();

    if (!inlineZoomActive) {
      // Zoom In logic for both Desktop & Mobile
      inlineZoomActive = true;
      host.classList.add("product-media__zoom--active");

      host.style.touchAction = "none";

      hiResPromise = preloadHiRes(img, host);
      void hiResPromise.then(() => {
        updateDesktopZoom(host, img, e.clientX, e.clientY);
      });
      document.addEventListener("pointerdown", onDocPointerDown, true);
    } else {
      closeInlineZoom();
    }
  };

  const onPointerMove = (e) => {
    if (!inlineZoomActive) return;
    void hiResPromise.then(() => {
      updateDesktopZoom(host, img, e.clientX, e.clientY);
    });
  };

  const onPointerLeave = (e) => {
    if (e.pointerType === "touch") return;
    if (!inlineZoomActive) return;
    closeInlineZoom();
  };

  host.addEventListener("click", onHostClick);
  host.addEventListener("pointermove", onPointerMove);
  host.addEventListener("pointerleave", onPointerLeave);
  host.addEventListener("touchmove", (e) => {
    if (inlineZoomActive) {
      e.preventDefault();
    }
  }, { passive: false });
}
function scan(root) {
  root.querySelectorAll("[data-product-image-zoom]").forEach(bindHost);
}

function init() {
  scan(document);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

const observer = new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (node.nodeType !== 1) continue;
      if (node.matches?.("[data-product-image-zoom]")) {
        bindHost(node);
      }
      if (node.querySelectorAll) {
        scan(node);
      }
    }
  }
});

observer.observe(document.documentElement, { childList: true, subtree: true });
